import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Nav from '../../components/Nav/Nav';
import { ContextTicketDto, TicketStatusNameType } from '../HomeView/HomeView';
import { getStatusColor } from '../../utils/GetStatusColor';

const TicketSuccessView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<ContextTicketDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ticketGuid = searchParams.get('ticket_guid');

  useEffect(() => {
    if (!ticketGuid) {
      setError('No ticket specified');
      setLoading(false);
      return;
    }
    fetchTicket();
  }, [ticketGuid]);

  const fetchTicket = async () => {
    if (!ticketGuid) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${process.env.API_HOST || ''}/api/v1/tickets?Ticket_guid=${ticketGuid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ticket: ${response.status} ${response.statusText}`);
      }

      const ticketsData: ContextTicketDto[] = await response.json();

      const matchedTicket = ticketsData.find(
        (item) => item.ticket.Ticket_guid === ticketGuid,
      );

      if (!matchedTicket) {
        throw new Error('Ticket not found');
      }

      setTicket(matchedTicket);
    } catch (err) {
      console.error("Error fetching ticket:", err);
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleViewTicket = () => {
    navigate(`/ticket?Ticket_guid=${ticket?.ticket.Ticket_guid}`);
  };

  const handleBackHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <>
        <Nav />
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (error || !ticket) {
    return (
      <>
        <Nav />
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Ticket not found'}
          </Alert>
          <Button
            variant="outlined"
            onClick={handleBackHome}
            startIcon={<HomeIcon />}
          >
            Back to Home
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <Nav />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          {/* Success Header */}
          <Box sx={{ mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h3" component="h1" color="success.main" gutterBottom>
              Ticket Created Successfully!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Your traffic ticket representation request has been submitted
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We've received your payment and ticket information. Our legal team will begin working on your case shortly.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Ticket Information Card */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <ReceiptIcon />
                Ticket Details
              </Typography>
              
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Ticket Number:
                  </Typography>
                  <Typography variant="body1">
                    {ticket.ticket.Ticket_Number}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Citation:
                  </Typography>
                  <Typography variant="body1">
                    {ticket.ticket.Ticket_Citation}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Status:
                  </Typography>
                  <Chip
                    label={ticket.ticketStatus?.TicketStatus_Name || 'citation received (Approving)'}
                    color={getStatusColor(ticket.ticketStatus?.TicketStatus_Name || 'citation received (Approving)')}
                    size="small"
                  />
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarTodayIcon fontSize="small" />
                    Court Date:
                  </Typography>
                  <Typography variant="body1">
                    {ticket.ticket.Ticket_CourtDate ? formatDate(ticket.ticket.Ticket_CourtDate) : 'Not set'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card elevation={1} sx={{ mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                What Happens Next?
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'left' }}>
                • Our legal team will review your case within 24 hours
                <br />
                • You'll receive email updates on your case progress
                <br />
                • We'll handle all court communications on your behalf
                <br />
                • You can track your case status anytime in your dashboard
              </Typography>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={handleViewTicket}
              startIcon={<VisibilityIcon />}
              size="large"
            >
              View Ticket Details
            </Button>
            <Button
              variant="outlined"
              onClick={handleBackHome}
              startIcon={<HomeIcon />}
              size="large"
            >
              Back to Dashboard
            </Button>
          </Stack>

          {/* Confirmation Message */}
          <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Important:</strong> Please save your ticket number ({ticket.ticket.Ticket_Number}) for your records. 
              You will receive a confirmation email shortly with additional details about your case.
            </Typography>
          </Alert>
        </Paper>
      </Container>
    </>
  );
};

export default TicketSuccessView;
